import { useOutletContext } from "react-router-dom";
import { useTheme } from "@emotion/react";
import useStyles from "./styles";
import { Alert, Box, Snackbar } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";

import SessoesBgp from "./sessoes";
import LinksBgp from "./linksBgp/indesx";
import NoHostScreen from "../noHostScreen";
import buscarMetricasDisponiveis from "../../../../utils/validarMetricas";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);

// Texto pré-quebrado: é desenhado dentro do SVG da ilustração, que não quebra
// linha sozinho (máx. ~28 caracteres por linha)
const SEM_DADOS_BGP = `Este equipamento não possui\nlinks ou sessões BGP\nconfigurados.`;

export default function DashBgp({
  findLinksUrl = "/find/resumoLinksBgp",
  serieTemporalUrl = "/zabbix/serieTemporal",
  findHostsUrl = "/interno/findMany/hostsCliente",
  findInterfaceListUrl = "/zabbix/interfaceList",
  findItemsListUrl = "/zabbix/items/list",
  findPrefixSessionsUrl = "/find/prefixSessions",
}) {
  const {
    api,
    apiCliente,
    filterData,
    handleAplicarZoom,
    optionsTime,
    setCounter,
    selectTime,
    stopCronometro,
    startCronometro,
    controllerDashboard,
    setLoadingCronometro,
    setControllerDashboard,
  } = useOutletContext();
  const apiReq = api || apiCliente;
  const theme = useTheme();
  const styles = useStyles(theme);
  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [hostsCliente, setHostsCliente] = useState({});
  const lastFetchedClienteId = useRef(null);
  const [interfaceDescriptions, setInterfaceDescriptions] = useState({});
  const [links, setLinks] = useState({
    trafficIn: {},
    trafficOut: {},
    trafficInLastItem: {},
    trafficOutLastItem: {},
  });
  const [sessions, setSessions] = useState({
    receivedPrefixes: {},
    announcedPrefixes: {},
    bgpUptimeLastItem: {},
    bgpStatusLastItem: {},
    receivedPrefixesLastItem: {},
    announcedPrefixesLastItem: {},
  });
  const prefixSessionsControllerRef = useRef(null);

  // Equipamento sem nada para mostrar (nem link, nem sessão)
  const [semDados, setSemDados] = useState(false);

  const clearData = () => {
    setSemDados(false);
    setLinks({
      trafficIn: {},
      trafficOut: {},
      trafficInLastItem: {},
      trafficOutLastItem: {},
    });
    setSessions({
      receivedPrefixes: {},
      announcedPrefixes: {},
      bgpUptimeLastItem: {},
      bgpStatusLastItem: {},
      receivedPrefixesLastItem: {},
      announcedPrefixesLastItem: {},
    });
    setInterfaceDescriptions({});
  };

  useEffect(() => {
    return () => {
      prefixSessionsControllerRef?.current?.abort?.();
    };
  }, []);

  useEffect(() => {
    if (
      filterData?.initialDate &&
      filterData?.finalDate &&
      filterData?.device &&
      filterData?.serviceId &&
      filterData?.hostId
    ) {
      fetchAllData();
    } else {
      clearData();
    }
  }, [filterData]);

  useEffect(() => {
    return () => {
      if (controllerDashboard) {
        controllerDashboard?.abort();
      }
      stopCronometro();
    };
  }, []);

  // Montar objeto e chamar todas as funções necessárias
  async function fetchAllData() {
    setLoading(true);
    // Resetar o cronometro
    setCounter(null);
    stopCronometro();
    setLoadingCronometro(true);

    // Obter parametros dos filtros
    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;
    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime[selectTime]?.value;
      finalDate = dayjs()?.set("second", 0)?.set("millisecond", 0)?.toDate();
    }
    if (controllerDashboard) {
      controllerDashboard?.abort();
    }
    const newController = new AbortController();
    const signal = newController.signal;
    setControllerDashboard(newController);

    console.log(filterData);

    // Parâmetros comuns a diferentes consultas
    const commonSearchParams = {
      device: filterData?.device,
      initial_date: dateInitial,
      final_date: finalDate,
    };

    /** 
      Realizar consultas com await 
      para garantir o funcionamento 
      correto do cronometro 
    */
    let hostsData = hostsCliente;
    if (
      lastFetchedClienteId.current !== filterData?.clienteId ||
      !Object.keys(hostsCliente).length
    ) {
      hostsData = await getHosts(signal);

      // Atualizar a ref
      if (hostsData) {
        lastFetchedClienteId.current = filterData?.clienteId;
      }
    }
    // Métricas de sessão que o equipamento tem de fato (schema + item no Zabbix)
    const metricasSessao = await buscarMetricasDisponiveis({
      api: apiReq,
      filterData,
      dash: "linkSession",
      signal,
      itemsListUrl: findItemsListUrl,
    });
    if (signal.aborted) return;

    /**
     * CONSULTAR
     */
    const [linksData] = await Promise.all([
      getLinks({ commonSearchParams, signal }),
      getSessions({ commonSearchParams, signal, hostsData }),
    ]);

    /**
     * Só declara "sem dados" quando não há NADA a exibir. Os links não vêm do
     * schema (findLinksUrl é consulta própria), então a validação de sessão
     * sozinha não pode decidir — senão esconderia links que funcionam.
     */
    const temLink =
      Object.keys(linksData?.trafficIn || {}).length > 0 ||
      Object.keys(linksData?.trafficOut || {}).length > 0;
    // metricasSessao null = validação indisponível → assume que tem (fail-open)
    const temSessao =
      !Array.isArray(metricasSessao) || metricasSessao.length > 0;

    // Inicializar o cronometro
    setLoading(false);
    if (!signal.aborted) {
      setSemDados(!temLink && !temSessao);
      setLoadingCronometro(false);
      startCronometro();
    }
  }

  async function getLinks({ commonSearchParams, signal }) {
    try {
      setLoadingLinks(true);

      const [links, interfaceDescriptions] = await Promise.all([
        apiReq.post(
          findLinksUrl,
          {
            ...commonSearchParams,
            clienteId: filterData?.clienteId,
            servicoId: filterData?.serviceId,
            hostId: filterData?.hostId,
          },
          { signal },
        ),
        apiReq.post(
          findInterfaceListUrl,
          {
            hostId: filterData?.hostId,
          },
          { signal },
        ),
      ]);

      setLinks(links?.data || {});

      const interfaceDescriptionsMap = interfaceDescriptions?.data?.reduce(
        (acc, item) => {
          acc[item?.name] = item?.description;
          return acc;
        },
        {},
      );

      setInterfaceDescriptions(interfaceDescriptionsMap);

      // Devolvido para o fetchAllData decidir sobre o estado vazio
      return links?.data || {};
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Falha ao buscar os links.",
        severity: "error",
      });
    } finally {
      setLoadingLinks(false);
    }
  }

  async function getSessions({ commonSearchParams, signal, hostsData }) {
    try {
      setLoadingSessions(true);
      const sessaoConsulta = hostsData?.sessions?.[filterData?.serviceId]?.find(
        (s) => s?.hostId === filterData?.hostId,
      );

      const interfaces = sessaoConsulta?.hostsClienteInterfaces;
      if (!sessaoConsulta || !interfaces?.length) {
        return;
      }

      const hostId = sessaoConsulta?.hostId;

      const requests = [
        {
          nameFilter: "receivedPrefixes",
          stateKey: "receivedPrefixes",
        },
        {
          nameFilter: "announcedPrefixes",
          stateKey: "announcedPrefixes",
        },
        {
          nameFilter: "bgpUptime",
          lastItem: true,
          stateKey: "bgpUptimeLastItem",
        },
        {
          nameFilter: "bgpStatus",
          lastItem: true,
          stateKey: "bgpStatusLastItem",
        },
        {
          nameFilter: "receivedPrefixes",
          lastItem: true,
          stateKey: "receivedPrefixesLastItem",
        },
        {
          nameFilter: "announcedPrefixes",
          lastItem: true,
          stateKey: "announcedPrefixesLastItem",
        },
      ];

      await Promise.all([
        ...requests?.map(({ nameFilter, lastItem, stateKey }) =>
          apiReq
            .post(
              serieTemporalUrl,
              {
                ...commonSearchParams,
                hostids: hostId,
                nameFilter,
                ...(lastItem ? { lastItem: true } : {}),
                interface: interfaces,
                forceArray: true,
                fabricante: filterData?.fabricante,
                funcao: filterData?.funcao,
                modelo: filterData?.modelo,
                dash: "linkSession",
              },
              { signal },
            )
            .then((response) => {
              setSessions((prev) => ({
                ...prev,
                [stateKey]: {
                  ...prev[stateKey],
                  [hostId]: response?.data,
                },
              }));
            }),
        ),
      ]);
    } catch (error) {
      console.error(error);
      setSessions({});
      setSnackbar({
        children: "Falha ao buscar as sessões.",
        severity: "error",
      });
    } finally {
      setLoadingSessions(false);
    }
  }

  /**
   * Busca prefixos recebidos apenas da sessão/interface solicitada.
   * Disparado sob demanda (clique no botão "Ver Lista"), não no fluxo
   * automático de refresh/filtro.
   */
  async function fetchPrefixesForInterface({ host, interfaceName }) {
    prefixSessionsControllerRef?.current?.abort?.();
    const newController = new AbortController();
    prefixSessionsControllerRef.current = newController;

    try {
      const response = await apiReq?.post?.(
        findPrefixSessionsUrl,
        {
          host: host || filterData?.device,
          device: filterData?.device,
          interfaces: [interfaceName],
        },
        { signal: newController?.signal },
      );
      return response?.data || [];
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("[DashBgp] Erro ao buscar prefixSessions:", error);
        setSnackbar({
          children: "Falha ao buscar os prefixos recebidos.",
          severity: "error",
        });
      }
      return [];
    }
  }

  async function getHosts(signal) {
    try {
      setLoading(true);

      const response = await apiReq.post(
        findHostsUrl,
        {
          clienteId: filterData?.clienteId,
          includeEmptyHosts: true,
          mapHostsByServiceAndEquipmentType: true,
          linksAndSessions: true,
        },
        { signal },
      );

      setHostsCliente(response?.data || {});
      return response?.data || {};
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Falha ao buscar os hosts.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  if (semDados) {
    return <NoHostScreen mensagem={SEM_DADOS_BGP} />;
  }

  return (
    <Box sx={styles.container}>
      <LinksBgp
        links={links}
        loading={loading || loadingLinks}
        handleAplicarZoom={handleAplicarZoom}
        isZoomEnabled={true}
        interfaceDescriptions={interfaceDescriptions}
      />
      <SessoesBgp
        hostsCliente={hostsCliente}
        sessions={sessions}
        filterData={filterData}
        loading={loading || loadingSessions}
        fetchPrefixesForInterface={fetchPrefixesForInterface}
      />
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
